import type { ReactNode } from 'react';
import { memo, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import type { RelatedTaskSummary } from '@/types';
import type { Note } from '@/types/notes';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { NoteMetadataSection } from './NoteMetadataSection';
import styles from '../NoteDetailPanel.module.css';

const LazyNoteMiniMap = lazy(() =>
  import('@/components/map/NoteMiniMap').then((module) => ({
    default: module.NoteMiniMap,
  }))
);

interface NoteDetailSidebarAsideProps {
  note: Note;
  firstLinkedTask: RelatedTaskSummary | undefined;
  /** Omit to render the linked task as plain text (public, non-navigable). */
  onTaskNavigate?: (taskId: string, title: string, shortId?: string) => void;
  attachmentsContent: ReactNode;
}

export const NoteDetailSidebarAside = memo(function NoteDetailSidebarAside({
  note,
  firstLinkedTask,
  onTaskNavigate,
  attachmentsContent,
}: NoteDetailSidebarAsideProps) {
  const { t } = useTranslation();

  return (
    <aside className={styles.detailMetadataSidebar}>
      <AppScrollArea fill>
        <div className={styles.detailMetadataContent}>
          <div className={styles.sidebarSection}>
            <div className={styles.miniMapContainer}>
              <Suspense fallback={null}>
                <LazyNoteMiniMap
                  latitude={note.latitude ?? 0}
                  longitude={note.longitude ?? 0}
                  color={note.color}
                  mode="view"
                />
              </Suspense>
            </div>
          </div>

          <NoteMetadataSection
            note={note}
            linkedTask={firstLinkedTask}
            onTaskNavigate={onTaskNavigate}
          />

          {attachmentsContent && (
            <div className={styles.sidebarSection}>
              <span className={styles.sidebarSectionLabel}>
                {t('notes.detail.attachmentsLabel')}
              </span>
              {attachmentsContent}
            </div>
          )}
        </div>
      </AppScrollArea>
    </aside>
  );
});
